import { Pencil } from 'lucide-react';
import { useConfig } from '../context/ConfigContext';
import { formatMoney } from '../utils/currencyUtil';
import { posStockLevel, describePosUnits, posReserveUnits } from '../utils/posUtil';
import PosStockCell from './PosStockCell';
import '../css/Pos.css';

/**
 * Inventory's grid view: every product as a row of text.
 *
 * The counterpart to the card view rather than a replacement for it. Cards are
 * for finding a product you would recognise by its packaging; this is for
 * checking a shelf against a delivery note, where what matters is having every
 * figure lined up in a column and the stock count editable without opening
 * anything.
 *
 * THE STOCK COLUMN IS LIVE. Each cell writes through setPosStock, which is the
 * same path the product modal uses, so a correction made here is recorded on
 * the audit trail exactly as one made there — see PosStockCell.
 *
 * @param {object} props
 * @param {object[]} props.products - already filtered and sorted by the screen
 * @param {string} props.clientId
 * @param {string} [props.staffEmail] - recorded against a stock correction
 * @param {boolean} [props.canEdit] - the "add stocks" permission. Without it
 *   the counts render as text and the Edit column is dropped entirely.
 * @param {(product: object) => void} props.onEdit
 * @param {(error: Error) => void} [props.onError]
 * @param {string} [props.scope='active'] - 'active' lists the shelf with its
 *   count editable in place; 'reserve' lists the storeroom in packs and pieces
 *   with a Replenish action. Same table, because they are the same products —
 *   only the last two columns differ.
 * @param {(product: object) => void} [props.onReplenish] - on the Reserve tab,
 *   opens the action chooser (replenish the shelf, or edit the reserve)
 * @param {(id: string) => object} [props.getDragProps] - from useDragReorder,
 *   when the list is being arranged by hand
 * @param {string|null} [props.draggingId]
 * @returns {JSX.Element}
 */
function PosProductTable({
  products, clientId, staffEmail = '', canEdit = false, onEdit, onError,
  scope = 'active', onReplenish, getDragProps = () => ({}), draggingId = null,
}) {
  const isReserve = scope === 'reserve';
  const { config } = useConfig();

  return (
    <div className="pos-table-scroll">
      <table className="pos-invtable">
        <thead>
          <tr>
            <th scope="col">Product</th>
            <th scope="col">Description</th>
            <th scope="col">Category</th>
            <th scope="col" className="num">Price</th>
            <th scope="col" className={isReserve ? '' : 'num'}>{isReserve ? 'In reserve' : 'Stock'}</th>
            {canEdit && <th scope="col" className="pos-invtable-actions"><span className="pos-sr-only">Edit</span></th>}
          </tr>
        </thead>
        <tbody>
          {products.map((product) => {
            const tone = posStockLevel(product, config?.lowStockThreshold);
            return (
              <tr
                key={product.id}
                className={draggingId === product.id ? 'dragging' : undefined}
                {...getDragProps(product.id)}
              >
                <th scope="row" className="pos-invtable-name">{product.name}</th>
                {/* Em dash rather than an empty cell: a blank reads as a
                    column that failed to render, a dash as a field nobody
                    filled in. */}
                <td className="pos-invtable-desc">
                  {product.description || <span className="pos-invtable-none">&mdash;</span>}
                </td>
                <td>
                  {product.category
                    ? <span className="pos-invtable-cat">{product.category}</span>
                    : <span className="pos-invtable-none">&mdash;</span>}
                </td>
                {/* A product registered on the Reserve tab has no price until
                    someone sets one here, so an unpriced item on the SHELF is
                    worth seeing before a customer is charged nothing for it.
                    Flagged rather than blocked — a giveaway is a real thing to
                    sell — and only on the Active tab, where Edit can fix it. */}
                <td className="num">
                  {!isReserve && !(Number(product.price) > 0)
                    ? <span className="pos-invtable-noprice">No price</span>
                    : formatMoney(Number(product.price || 0), config)}
                </td>

                {/* In words, not a bare number: a storeroom is read as boxes
                    and a few loose, and 82 tells nobody how many boxes that
                    is. Not editable here — the storeroom is corrected from the
                    product form, so there is one path and one audit entry for
                    it, the same rule the shelf follows. */}
                {isReserve ? (
                  <td className={posReserveUnits(product) === 0 ? 'pos-invtable-none' : undefined}>
                    {describePosUnits(posReserveUnits(product), product)}
                  </td>
                ) : (
                  <td className="num">
                    <PosStockCell
                      clientId={clientId}
                      product={product}
                      actor={staffEmail}
                      disabled={!canEdit}
                      tone={tone}
                      onError={onError}
                    />
                  </td>
                )}

                {canEdit && (
                  <td className="pos-invtable-actions">
                    {isReserve ? (
                      <button
                        type="button"
                        className="pos-invtable-edit"
                        data-no-drag
                        onClick={() => onReplenish(product)}
                        title={`Replenish the shelf or edit the reserve for ${product.name}`}
                      >
                        Manage
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="pos-invtable-edit"
                        data-no-drag
                        onClick={() => onEdit(product)}
                        aria-label={`Edit ${product.name}`}
                      >
                        <Pencil size={13} /> Edit
                      </button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default PosProductTable;
